import { useState, useEffect, useRef } from "react";
import { SearchQueryVariables, fetchSearchResultsQuery } from "../queries/SearchQuery";
import { SearchResultDisplayData } from "../SearchResultRow/SearchResultRow.types";
import { getValidRecordsDisplayData } from "../utils/SearchResultRow.utils";
import { IntlShape } from "react-intl";
import { MINIMUM_SEARCH_TERM_LENGTH } from "../constants/SearchDialog.constants";

export const useFetchSearchResultsQuery = ({
  variables,
  searchInput,
  intl,
}: {
  variables: SearchQueryVariables;
  searchInput: string;
  intl: IntlShape;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<SearchResultDisplayData[]>([]);
  const [searchError, setSearchError] = useState<Error | null>(null);
  const hasMinSearchTerm = searchInput.length > MINIMUM_SEARCH_TERM_LENGTH;
  const abortControllerRef = useRef(new AbortController());

  useEffect(() => {
    if (hasMinSearchTerm) {
      setIsLoading(true);
      abortControllerRef.current.abort();
      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;

      fetchSearchResultsQuery(variables, abortControllerRef.current.signal)
        .then((data) => {
          setIsLoading(false);
          setSearchError(null);
          setSearchResults(data ? getValidRecordsDisplayData(data.searchAsync, intl) : []);
        })
        .catch((error: unknown) => {
          if (error instanceof Error && error.name !== "AbortError") {
            setIsLoading(false);
            console.error(error);
            setSearchError(error);
          }
          setSearchResults([]);
        });
    } else {
      setSearchResults([]);
    }
    return () => {
      abortControllerRef.current.abort();
    };
  }, [searchInput, variables, hasMinSearchTerm, intl]);

  return { isLoading, searchResults, searchError };
};
